import { Stepper as MatterStepper } from "@allaria-tech/matter/Stepper";
import type { StepperStep } from "@allaria-tech/matter/Stepper";
import { Step as StepType } from "../../types";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  onUpdateStep,
  onUpdateSubStep,
} from "../../reducers/onboardingDataReducer";
import {
  onUpdateStep as onUpdateStepSecurities,
  onUpdateSubStep as onUpdateSubStepSecurities,
} from "../../reducers/securitiesReducer";
import useWindowWidth from "../../hooks/useWindowWidth";
import {
  getNextNavigableSubStep,
  isBackNavigationDisabled,
  isStepSkipped,
} from "../TemplateInterpreter/utils";

interface Props {
  testId?: string;
}

const Stepper = ({ testId = "stepper" }: Props) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state);
  const { onboardingData, securities, activeTemplate: template } = state;

  const { width } = useWindowWidth();
  const isSmallDevice = width < 1280;

  const isSecurities = template.onboardingType === "SECURITIES";

  const { completedSteps, reachedSteps, step } = isSecurities
    ? securities.stepData
    : onboardingData.stepData;

  const formValues =
    (isSecurities ? securities.formValues : onboardingData.formValues) ??
    undefined;

  const numericTemplateSteps =
    template?.steps?.filter(
      ({ step: templateStep, hideFromStepper }) =>
        typeof templateStep === "number" && !hideFromStepper,
    ) ?? [];

  const isCompleted = (findStep: number) =>
    !!completedSteps?.includes(findStep);

  const getIsDisabled = (
    stepNumber: number,
    disableBackNavigation: StepType["disableBackNavigation"],
  ) =>
    isBackNavigationDisabled(
      disableBackNavigation,
      formValues || {},
      isCompleted(stepNumber + 1) ? "completed" : "disabled",
    );

  const isSkipped = (templateStep: StepType) =>
    isStepSkipped(templateStep, formValues, state);

  if (!numericTemplateSteps.length) return null;

  const steps: StepperStep[] = numericTemplateSteps.map((templateStep) => {
    const stepNumber = Number(templateStep.step);
    const completed = isCompleted(stepNumber);

    return {
      label: templateStep.titleOfStep ?? templateStep.name ?? "",
      completed,
      locked:
        completed &&
        (isSkipped(templateStep) ||
          getIsDisabled(stepNumber, templateStep.disableBackNavigation)),
    };
  });

  const currentStep = numericTemplateSteps.findIndex(
    ({ step: templateStep }) => Number(templateStep) === step,
  );

  const handleStepClick = (index: number) => {
    const clicked = numericTemplateSteps[index];
    if (!clicked) return;

    const stepNumber = Number(clicked.step);
    if (!reachedSteps?.includes(stepNumber)) return;
    if (isSkipped(clicked)) return;

    dispatch(
      isSecurities
        ? onUpdateStepSecurities(stepNumber)
        : onUpdateStep(stepNumber),
    );

    if (clicked.subSteps) {
      const subStep =
        getNextNavigableSubStep(clicked, 1, formValues, state) ?? 1;
      dispatch(
        isSecurities
          ? onUpdateSubStepSecurities(subStep)
          : onUpdateSubStep(subStep),
      );
    }
  };

  return (
    <MatterStepper
      variant={isSmallDevice ? "simple" : "extended"}
      display={isSmallDevice ? "horizontal" : "vertical"}
      steps={steps}
      currentStep={currentStep}
      onStepClick={handleStepClick}
      ariaLabel="Progreso"
      testId={testId}
    />
  );
};

export default Stepper;
